import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import './InstagramFeedSection.css'
import { IG_POSTS } from './igFeedData'

const AUTOPLAY_MS = 4500
const HEART_PATH =
  'M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z'
const COMMENT_PATH = 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'
const SAVE_PATH = 'm19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z'

function getVisible() {
  if (typeof window === 'undefined') return 3
  if (window.innerWidth >= 1024) return 3
  if (window.innerWidth >= 640) return 2
  return 1
}

function TypeBadgeIcon({ type }) {
  if (type === 'Reel') {
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
        <polygon points="23 7 16 12 23 17 23 7" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      </svg>
    )
  }
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  )
}

function IgPostCard({ post }) {
  return (
    <div className="ig-slide">
      <div className="ig-card">
        <div className="ig-card-header">
          <div className="ig-avatar-ring">
            <div className="ig-avatar-inner">R</div>
          </div>
          <div>
            <div className="ig-handle">astrologerrajumaharajji</div>
            <div className="ig-handle-sub">Vedic Astrologer</div>
          </div>
          <div className="ig-card-follow">Follow</div>
        </div>
        <div className="ig-img-wrap">
          <img className="ig-img" src={post.img} alt={post.alt} loading="lazy" />
          <div className="ig-type-badge">
            <TypeBadgeIcon type={post.type} />
            {post.type}
          </div>
          <div className="ig-img-overlay">
            <div className="ig-overlay-hearts">
              <div className="ig-overlay-stat">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" stroke="none">
                  <path d={HEART_PATH} />
                </svg>
                {post.likes}
              </div>
              <div className="ig-overlay-stat">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <path d={COMMENT_PATH} />
                </svg>
                {post.comments}
              </div>
            </div>
          </div>
        </div>
        <div className="ig-card-footer">
          <div className="ig-actions">
            <div className="ig-action">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d={HEART_PATH} />
              </svg>
              {post.likes}
            </div>
            <div className="ig-action">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d={COMMENT_PATH} />
              </svg>
              {post.comments}
            </div>
            <div className="ig-action ig-action-save">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d={SAVE_PATH} />
              </svg>
            </div>
          </div>
          <div className="ig-caption">
            <strong>astrologerrajumaharajji</strong> {post.caption}
          </div>
          <div className="ig-time">{post.time}</div>
        </div>
      </div>
    </div>
  )
}

export default function InstagramFeedSection() {
  const sectionRef = useRef(null)
  const trackRef = useRef(null)
  const timerRef = useRef(null)
  const pausedRef = useRef(false)
  const touchStartRef = useRef(0)

  const [visible, setVisible] = useState(getVisible)
  const [current, setCurrent] = useState(0)
  const [slideW, setSlideW] = useState(0)
  const [entered, setEntered] = useState(false)

  const total = IG_POSTS.length
  const maxIdx = Math.max(0, total - visible)

  useLayoutEffect(() => {
    const measure = () => {
      const el = trackRef.current?.children?.[0]
      if (el) setSlideW(el.getBoundingClientRect().width)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [visible])

  useEffect(() => {
    const onResize = () => setVisible(getVisible())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    setCurrent((c) => Math.min(c, maxIdx))
  }, [maxIdx])

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setEntered(true)
        observer.disconnect()
      },
      { threshold: 0.08 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const stop = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const start = useCallback(() => {
    stop()
    if (pausedRef.current) return
    timerRef.current = window.setInterval(() => {
      setCurrent((c) => (c >= maxIdx ? 0 : c + 1))
    }, AUTOPLAY_MS)
  }, [stop, maxIdx])

  useEffect(() => {
    start()
    return stop
  }, [start, stop])

  const goTo = (idx) => setCurrent(Math.max(0, Math.min(maxIdx, idx)))

  const next = () => {
    goTo(current >= maxIdx ? 0 : current + 1)
    stop()
    start()
  }
  const prev = () => {
    goTo(current <= 0 ? maxIdx : current - 1)
    stop()
    start()
  }

  const handleMouseEnter = () => {
    pausedRef.current = true
    stop()
  }
  const handleMouseLeave = () => {
    pausedRef.current = false
    start()
  }
  const handleTouchStart = (e) => {
    touchStartRef.current = e.changedTouches[0].clientX
  }
  const handleTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartRef.current
    if (Math.abs(dx) > 40) {
      if (dx < 0) next()
      else prev()
    }
  }

  return (
    <section id="ig-feed" ref={sectionRef}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.03 }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="ig-dots-pattern" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="#B45309" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#ig-dots-pattern)" />
        </svg>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 50% 55% at 90% 40%,rgba(193,53,132,0.07) 0%,transparent 65%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 45% 50% at 10% 80%,rgba(245,96,64,0.05) 0%,transparent 60%)' }} />
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '400px', height: '400px', opacity: 0.05 }}>
          <svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
            <circle cx="200" cy="200" r="180" fill="none" stroke="#B45309" strokeWidth="1" />
            <circle cx="200" cy="200" r="136" fill="none" stroke="#D97706" strokeWidth="0.7" />
            <circle cx="200" cy="200" r="92" fill="none" stroke="#F59E0B" strokeWidth="0.5" strokeDasharray="4 6" />
          </svg>
        </div>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(193,53,132,0.24),transparent)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(180,83,9,0.22),transparent)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-4" style={{ marginBottom: '40px' }}>
          <div className={`ig-in${entered ? ' ig-on' : ''}`} style={{ animationDelay: '.04s' }}>
            <div className="ig-platform-badge">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="ig-ico" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#F56040" />
                    <stop offset="50%" stopColor="#E1306C" />
                    <stop offset="100%" stopColor="#833AB4" />
                  </linearGradient>
                </defs>
                <rect x="2" y="2" width="20" height="20" rx="5.5" fill="url(#ig-ico)" />
                <circle cx="12" cy="12" r="4.5" fill="none" stroke="#fff" strokeWidth="1.5" />
                <circle cx="17.2" cy="6.8" r="1" fill="#fff" />
              </svg>
              <span>Instagram Feed</span>
            </div>
            <h2 className="ig-heading">
              Follow Us on <span>Instagram</span>
            </h2>
            <p className="ig-sub">Reels, sacred knowledge, gemstone reveals &amp; behind-the-scenes moments — all on our Instagram.</p>
          </div>
          <a href="#" className={`ig-follow-btn ig-in${entered ? ' ig-on' : ''}`} style={{ animationDelay: '.10s' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <defs>
                <linearGradient id="ig-btn-ico" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#F56040" />
                  <stop offset="100%" stopColor="#C13584" />
                </linearGradient>
              </defs>
              <rect x="2" y="2" width="20" height="20" rx="5.5" fill="url(#ig-btn-ico)" />
              <circle cx="12" cy="12" r="4.5" fill="none" stroke="#fff" strokeWidth="1.5" />
              <circle cx="17.2" cy="6.8" r="1" fill="#fff" />
            </svg>
            Follow on Instagram
          </a>
        </div>

        <div
          className={`ig-viewport ig-in${entered ? ' ig-on' : ''}`}
          style={{ animationDelay: '.14s' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div ref={trackRef} className="ig-track" style={{ transform: `translateX(${-(current * slideW)}px)` }}>
            {IG_POSTS.map((post) => (
              <IgPostCard post={post} key={post.id} />
            ))}
          </div>
        </div>

        <div className="ig-dots">
          {Array.from({ length: maxIdx + 1 }, (_, i) => (
            <button
              key={i}
              type="button"
              className={`ig-dot${i === current ? ' ig-dot-active' : ''}`}
              style={{ width: i === current ? '22px' : '6px' }}
              onClick={() => {
                goTo(i)
                stop()
                start()
              }}
              aria-label={`Go to Instagram post page ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
